/**
 * Interface strings, Arabic and English.
 *
 * One flat dictionary per language rather than files per screen: the set is
 * small enough to read in one sitting, and having both languages side by side
 * is what makes a missing or mistranslated string obvious.
 *
 * `t()` interpolates {name}-style placeholders. Keep them named, never
 * positional — word order differs between the two languages, and a positional
 * placeholder silently produces nonsense when it moves.
 */

export const TRANSLATIONS = {
  ar: {
    'brand.name': 'مسارات',
    'common.loading': 'جارٍ التحميل…',
    'common.cancel': 'إلغاء',
    'common.optional': '(اختياري)',
    'common.signOut': 'تسجيل الخروج',
    'common.language': 'اللغة',
    'common.arabic': 'العربية',
    'common.english': 'English',
    'common.unexpected': 'حدث خطأ غير متوقع.',
    'common.offline': 'تعذّر الاتصال. تحقق من الإنترنت وحاول مجددًا.',
    'common.reviewFields': 'يرجى مراجعة الحقول.',
    'common.showPassword': 'إظهار كلمة المرور',
    'common.hidePassword': 'إخفاء كلمة المرور',

    'login.parentTitle': 'تسجيل دخول ولي الأمر',
    'login.studentTitle': 'تسجيل دخول الطالب',
    'login.parentSubtitle': 'تابع تقدّم أبنائك وأدر مناهجهم.',
    'login.studentSubtitle': 'ادخل لتبدأ درس اليوم.',
    'login.who': 'من يسجّل الدخول؟',
    'login.asParent': 'ولي أمر',
    'login.asStudent': 'طالب',
    'login.email': 'البريد الإلكتروني',
    'login.username': 'اسم المستخدم',
    'login.usernameHint': 'مثال: layan.m',
    'login.password': 'كلمة المرور',
    'login.submit': 'تسجيل الدخول',
    'login.pending': 'جارٍ الدخول…',
    'login.noAccount': 'ليس لديك حساب؟',
    'login.createAccount': 'أنشئ حساب ولي أمر',
    'login.studentFooter': 'اسم المستخدم وكلمة المرور من ولي أمرك.',

    'register.title': 'أنشئ حساب ولي الأمر',
    'register.subtitle': 'حساب واحد يدير أبناءك ومناهجهم ومتابعة تقدّمهم.',
    'register.name': 'اسمك',
    'register.email': 'البريد الإلكتروني',
    'register.password': 'كلمة المرور',
    'register.passwordHint': '{min} خانات على الأقل.',
    'register.submit': 'إنشاء الحساب',
    'register.haveAccount': 'لديك حساب؟',
    'register.signIn': 'سجّل الدخول',
    'register.childNote':
      'لا نطلب أي بيانات عن طفلك في هذه الخطوة. بيانات الطالب تُضاف لاحقًا، وباسم مستعار إن رغبت.',
    'register.doneTitle': 'تحقق من بريدك',
    'register.doneSubtitle': 'أرسلنا رابط التأكيد إلى {email}. الرابط صالح لمدة ٢٤ ساعة.',
    'register.doneSpam': 'إذا لم تجد الرسالة خلال دقائق، تحقق من مجلد الرسائل غير المرغوب فيها.',
    'register.backToLogin': 'العودة إلى تسجيل الدخول',

    'verify.working': 'جارٍ تأكيد بريدك',
    'verify.wait': 'لحظة من فضلك…',
    'verify.doneTitle': 'تم تأكيد بريدك',
    'verify.doneSubtitle': 'حسابك جاهز الآن.',
    'verify.doneBody': 'يمكنك تسجيل الدخول والبدء بإضافة أبنائك.',
    'verify.goSignIn': 'تسجيل الدخول',
    'verify.failTitle': 'تعذّر تأكيد البريد',
    'verify.failSubtitle': 'لا يزال بإمكانك تسجيل الدخول، وسنتيح لك طلب رابط جديد.',
    'verify.expired': 'رابط التأكيد غير صالح أو انتهت صلاحيته، أو تم استخدامه من قبل.',
    'verify.incomplete': 'الرابط غير مكتمل. افتح الرابط من رسالة البريد كما هو.',
    'verify.goToLogin': 'الذهاب إلى تسجيل الدخول',

    'nav.overview': 'نظرة عامة',
    'nav.students': 'الطلاب',
    'nav.lessons': 'الدروس',
    'nav.skills': 'المهارات',
    'nav.curriculum': 'المنهج',
    'nav.sections': 'أقسام لوحة ولي الأمر',

    'notice.inactiveTitle': 'الحساب غير مُفعّل.',
    'notice.inactiveBody':
      'أرسلنا رابط التأكيد إلى {email}. لا يمكن رفع منهج أو إنشاء خطة تعلّم قبل تأكيد البريد، ويمكنك حتى ذلك الحين إضافة أبنائك وتجهيز ملفاتهم.',

    'overview.welcome': 'مرحبًا بك',
    'overview.today': 'هذا ما تعلّمه {name} اليوم',
    'overview.emptyTitle': 'لنبدأ بإضافة طفلك',
    'overview.emptyBody':
      'أضف طفلًا أولًا، ثم ارفع منهجه لنستخرج منه المهارات وتعتمدها، فتبدأ الدروس اليومية. يمكنك إضافة أكثر من طفل، ولكل واحد منهجه ومتابعته المستقلة.',
    'overview.summaryFor': 'ملخص اليوم لـ {name}',
    'switcher.student': 'الطالب',

    'tiles.time': 'وقت التعلّم',
    'tiles.correct': 'إجابات صحيحة',
    'tiles.independent': 'إتقان مستقل',
    'tiles.none': 'لا يوجد بعد',

    'skills.progress': 'تقدم المهارات',
    'skills.empty':
      'لا توجد مهارات بعد لـ {name}. ارفع منهجه، ونستخرج منه المهارات وترتيبها، ثم تعتمدها أنت قبل أن تُستخدم في أي درس.',
    'skills.sectionBody':
      'تظهر هنا كل مهارة استخرجناها من منهجك، وحالة إتقانها، والدليل وراء كل حالة.',
    'status.mastered': 'متقنة',
    'status.assisted': 'بمساعدة',
    'status.review': 'مراجعة',

    'next.title': 'الدرس القادم',
    'next.empty':
      'سنختار الدرس القادم بناءً على ما أتقنه {name} وما احتاج فيه إلى مساعدة، ونشرح لك سبب الاختيار.',
    'lessons.body': 'ستظهر دروس اليوم هنا بعد اعتماد المنهج وإنشاء خطة التعلّم.',

    'curriculum.body': 'اختر منهج طفلك من قائمتنا، وسنبدأ الدروس منه.',
    'curriculum.browse': 'تصفّح المناهج',
    'curriculum.choose': 'اختر منهجًا',
    'curriculum.assign': 'إسناد إلى الطالب',
    'curriculum.assigning': 'جارٍ الإسناد…',
    'curriculum.assigned': 'تم إسناد المنهج.',
    'curriculum.remove': 'إزالة',
    'curriculum.none': 'لم تُسند أي مناهج بعد.',
    'curriculum.country': 'الدولة',
    'curriculum.track': 'نوع التعليم',
    'curriculum.grade': 'الصف',
    'curriculum.subject': 'المادة',
    'curriculum.forStudent': 'الطالب',
    'curriculum.lessons': '{n} درسًا',
    'curriculum.noneMatch': 'لا توجد مناهج مطابقة بعد. جرّب اختيارًا آخر.',
    'curriculum.notYet': 'لم نضف منهجك بعد؟ أخبرنا وسنعمل عليه.',
    'country.eg': 'مصر',
    'country.sa': 'السعودية',
    'track.national': 'المنهج الوطني',
    'track.american': 'الدبلومة الأمريكية',
    'track.british': 'البريطاني / IGCSE',
    'track.ib': 'البكالوريا الدولية',
    'track.azhar': 'الأزهري',
    'subject.math': 'الرياضيات',
    'subject.science': 'العلوم',
    'subject.arabic': 'اللغة العربية',
    'subject.english': 'اللغة الإنجليزية',
    'subject.social_studies': 'الدراسات الاجتماعية',
    'curriculum.needStudent': 'أضف طالبًا أولًا لتتمكن من رفع منهج له.',
    'curriculum.add': 'إضافة منهج',
    'curriculum.title': 'اسم المنهج',
    'curriculum.titleHint': 'مثال: رياضيات الصف الخامس، الفصل الأول.',
    'curriculum.student': 'الطالب',
    'curriculum.create': 'حفظ المنهج',
    'curriculum.created': 'تم إنشاء المنهج. ارفع ملف المنهج الآن.',
    'curriculum.file': 'ملف المنهج',
    'curriculum.fileHint': 'ملف PDF واحد، بحد أقصى {max} ميجابايت. يبقى الملف خاصًا بك.',
    'curriculum.uploadFile': 'رفع الملف',
    'curriculum.uploading': 'جارٍ الرفع…',
    'curriculum.uploaded': 'تم رفع الملف، وبدأت المعالجة.',
    'curriculum.for': 'لـ {name}',
    'curriculum.noneYet': 'لا توجد مناهج بعد.',
    'curriculum.unavailable': 'رفع الملفات غير متاح حاليًا. حاول لاحقًا.',
    'curriculum.pickFile': 'اختر ملفًا أولًا.',
    'status.upload_pending': 'بانتظار رفع الملف',
    'status.processing': 'قيد المعالجة',
    'status.needs_review': 'بانتظار مراجعتك',
    'status.approved': 'معتمد',
    'status.failed': 'تعذّرت المعالجة',
    'valid.content_type_invalid': 'يُقبل ملف PDF فقط.',
    'valid.file_too_large': 'حجم الملف {size} ميجابايت، والحد الأقصى {max} ميجابايت.',
    'valid.email_not_verified': 'فعّل بريدك الإلكتروني أولًا.',
    'curriculum.stage.queued': 'في الانتظار',
    'curriculum.stage.extracting': 'نقرأ الكتاب',
    'curriculum.stage.mapping': 'نحدّد الوحدات والدروس',
    'curriculum.stage.ready_for_review': 'جاهز لمراجعتك',
    'curriculum.stage.approved': 'تم الاعتماد',
    'curriculum.stage.failed': 'تعذّرت المعالجة',
    'curriculum.review': 'راجع الوحدات والدروس',
    'curriculum.reviewIntro': 'هذه هي الوحدات والدروس كما استخرجناها من الكتاب. تأكد من أرقام الصفحات قبل الاعتماد.',
    'curriculum.approve': 'اعتماد هذا التقسيم',
    'curriculum.approving': 'جارٍ الاعتماد…',
    'curriculum.approved': 'تم الاعتماد. سنبدأ الدروس من هنا.',
    'curriculum.pages': 'صفحات {from}-{to}',
    'curriculum.checkThese': 'نقاط تحتاج نظرة منك:',
    'curriculum.editRange': 'تعديل الصفحات',
    'curriculum.save': 'حفظ',
    'curriculum.cancel': 'إلغاء',
    'curriculum.alreadyAnalysed': 'هذا الكتاب محلَّل لدينا بالفعل، فالمراجعة جاهزة الآن.',
    'curriculum.failedBody': 'لم نتمكن من تحليل هذا الملف. تأكد أنه ملف PDF يحتوي على نص وليس صورًا ممسوحة ضوئيًا.',
    'curriculum.failed.too_large_to_extract': 'هذا الملف أكبر من أن نحلّله حاليًا (الحد ٤٠ ميجابايت). جرّب رفع الكتاب مقسّمًا إلى أجزاء، أو ملف الترم الواحد بدلًا من الكتاب كاملًا.',
    'curriculum.failed.no_text': 'يبدو أن هذا الملف صور ممسوحة ضوئيًا بلا نص. لا نستطيع قراءته بعد — نحتاج نسخة PDF تحتوي على نص.',
    'curriculum.failed.structure_invalid': 'قرأنا الملف لكن لم نتمكن من تحديد وحداته ودروسه بثقة. سنراجعه يدويًا.',
    'curriculum.failed.job_failed': 'تعثّرت المعالجة. سنعيد المحاولة، وإن تكرر ذلك سنراجعه يدويًا.',
    'curriculum.blocked': 'فعّل بريدك الإلكتروني أولًا لتتمكن من رفع منهج.',

    'students.title': 'الطلاب',
    'students.add': 'إضافة طالب',
    'students.empty':
      'لم تُضف أي طالب بعد. أضف طفلك لتتمكن من رفع منهجه ومتابعة تقدّمه، ويمكنك استخدام اسم مستعار.',
    'students.edit': 'تعديل',
    'students.remove': 'إزالة',
    'students.grade': 'الصف {grade}',
    'students.added': 'تمت إضافة الطالب.',
    'students.saved': 'تم حفظ التعديلات.',
    'students.removed': 'تمت إزالة الطالب.',

    'studentForm.addTitle': 'إضافة طالب',
    'studentForm.editTitle': 'تعديل {name}',
    'studentForm.name': 'اسم الطالب',
    'studentForm.nameHint': 'يمكن أن يكون اسمًا مستعارًا.',
    'studentForm.username': 'اسم المستخدم',
    'studentForm.usernameHint': 'ما سيكتبه الطالب عند الدخول. مثال: layan.m',
    'studentForm.usernameLocked': 'لا يمكن تغييره: طفلك حفظه، وتغييره يمنعه من الدخول.',
    'studentForm.gradeLabel': 'الصف',
    'studentForm.language': 'لغة واجهة الطالب',
    'studentForm.languageHint': 'لغة ما يراه طفلك. لا تغيّر لغتك أنت.',
    'studentForm.password': 'كلمة مرور الطالب',
    'studentForm.newPassword': 'كلمة مرور جديدة',
    'studentForm.passwordHintCreate':
      '{min} خانات على الأقل. أنت من يختارها، وللطالب لا يوجد بريد لاستعادتها.',
    'studentForm.passwordHintEdit':
      'اتركها فارغة إذا لم ترغب في تغييرها. تغييرها ينهي جلسات الطالب الحالية.',
    'studentForm.save': 'حفظ الطالب',
    'studentForm.saveEdits': 'حفظ التعديلات',

    'delete.title': 'إزالة {name}',
    'delete.body':
      'سيفقد {name} إمكانية الدخول فورًا. يبقى سجل تعلّمه محفوظًا لدينا، ويمكنك طلب حذفه نهائيًا من إعدادات الخصوصية.',
    'delete.confirmLabel': 'اكتب "{name}" للتأكيد',
    'delete.submit': 'إزالة الطالب',
    'delete.pending': 'جارٍ الإزالة…',

    'student.welcome': 'مرحبًا',
    'student.noLesson': 'لا يوجد درس جاهز بعد. اطلب من ولي أمرك إكمال إعداد المنهج.',

    'valid.required': 'هذا الحقل مطلوب.',
    'valid.invalid_email': 'أدخل بريدًا إلكترونيًا صالحًا.',
    'valid.password_too_short': 'كلمة المرور يجب أن تكون {min} خانات على الأقل.',
    'valid.password_invalid': 'كلمة المرور غير مقبولة.',
    'valid.username_format': 'حروف إنجليزية صغيرة وأرقام فقط، من ٣ إلى ٣٢ خانة.',
    'valid.username_taken': 'اسم المستخدم محجوز، اختر اسمًا آخر.',
    'valid.grade_invalid': 'اختر صفًا صحيحًا.',
    'valid.language_invalid': 'لغة غير مدعومة.',
    'valid.no_changes': 'لا يوجد تغيير.',
  },

  en: {
    'brand.name': 'msarat.ai',
    'common.loading': 'Loading…',
    'common.cancel': 'Cancel',
    'common.optional': '(optional)',
    'common.signOut': 'Sign out',
    'common.language': 'Language',
    'common.arabic': 'العربية',
    'common.english': 'English',
    'common.unexpected': 'Something went wrong.',
    'common.offline': 'Could not connect. Check your internet and try again.',
    'common.reviewFields': 'Please check the fields below.',
    'common.showPassword': 'Show password',
    'common.hidePassword': 'Hide password',

    'login.parentTitle': 'Parent sign in',
    'login.studentTitle': 'Student sign in',
    'login.parentSubtitle': "Follow your children's progress and manage their curricula.",
    'login.studentSubtitle': "Sign in to start today's lesson.",
    'login.who': 'Who is signing in?',
    'login.asParent': 'Parent',
    'login.asStudent': 'Student',
    'login.email': 'Email address',
    'login.username': 'Username',
    'login.usernameHint': 'For example: layan.m',
    'login.password': 'Password',
    'login.submit': 'Sign in',
    'login.pending': 'Signing in…',
    'login.noAccount': "Don't have an account?",
    'login.createAccount': 'Create a parent account',
    'login.studentFooter': 'Your username and password come from your parent.',

    'register.title': 'Create your parent account',
    'register.subtitle': "One account for your children, their curricula, and their progress.",
    'register.name': 'Your name',
    'register.email': 'Email address',
    'register.password': 'Password',
    'register.passwordHint': 'At least {min} characters.',
    'register.submit': 'Create account',
    'register.haveAccount': 'Already have an account?',
    'register.signIn': 'Sign in',
    'register.childNote':
      "We ask nothing about your child at this step. Student details come later, and a nickname is fine.",
    'register.doneTitle': 'Check your email',
    'register.doneSubtitle': 'We sent a confirmation link to {email}. It is valid for 24 hours.',
    'register.doneSpam': "If it has not arrived in a few minutes, check your spam folder.",
    'register.backToLogin': 'Back to sign in',

    'verify.working': 'Confirming your email',
    'verify.wait': 'One moment…',
    'verify.doneTitle': 'Your email is confirmed',
    'verify.doneSubtitle': 'Your account is ready.',
    'verify.doneBody': 'You can sign in and start adding your children.',
    'verify.goSignIn': 'Sign in',
    'verify.failTitle': 'Could not confirm your email',
    'verify.failSubtitle': 'You can still sign in, and request a new link from there.',
    'verify.expired': 'This confirmation link is invalid, expired, or already used.',
    'verify.incomplete': 'The link is incomplete. Open it from the email exactly as it is.',
    'verify.goToLogin': 'Go to sign in',

    'nav.overview': 'Overview',
    'nav.students': 'Students',
    'nav.lessons': 'Lessons',
    'nav.skills': 'Skills',
    'nav.curriculum': 'Curriculum',
    'nav.sections': 'Parent dashboard sections',

    'notice.inactiveTitle': 'Your account is not active.',
    'notice.inactiveBody':
      'We sent a confirmation link to {email}. You cannot upload a curriculum or create a learning plan until your email is confirmed. Until then you can still add your children and set up their profiles.',

    'overview.welcome': 'Welcome',
    'overview.today': 'What {name} learned today',
    'overview.emptyTitle': "Let's start by adding your child",
    'overview.emptyBody':
      "Add a child first, then upload their curriculum so we can extract the skills for you to approve, and the daily lessons begin. You can add more than one child, each with their own curriculum and progress.",
    'overview.summaryFor': "Today's summary for {name}",
    'switcher.student': 'Student',

    'tiles.time': 'Learning time',
    'tiles.correct': 'Correct answers',
    'tiles.independent': 'Independent mastery',
    'tiles.none': 'Nothing yet',

    'skills.progress': 'Skill progress',
    'skills.empty':
      'No skills for {name} yet. Upload their curriculum, and we will extract the skills and their order for you to approve before any lesson uses them.',
    'skills.sectionBody':
      'Every skill we extracted from your curriculum appears here, with its mastery status and the evidence behind it.',
    'status.mastered': 'Mastered',
    'status.assisted': 'With help',
    'status.review': 'Review',

    'next.title': 'Next lesson',
    'next.empty':
      'We will choose the next lesson from what {name} mastered and where they needed help, and explain why we chose it.',
    'lessons.body': "Today's lessons appear here once the curriculum is approved and a plan exists.",

    'curriculum.body': 'Choose your child’s curriculum from our list, and lessons will follow it.',
    'curriculum.browse': 'Browse curricula',
    'curriculum.choose': 'Choose a curriculum',
    'curriculum.assign': 'Assign to student',
    'curriculum.assigning': 'Assigning…',
    'curriculum.assigned': 'Curriculum assigned.',
    'curriculum.remove': 'Remove',
    'curriculum.none': 'No curricula assigned yet.',
    'curriculum.country': 'Country',
    'curriculum.track': 'Education track',
    'curriculum.grade': 'Grade',
    'curriculum.subject': 'Subject',
    'curriculum.forStudent': 'Student',
    'curriculum.lessons': '{n} lessons',
    'curriculum.noneMatch': 'Nothing matches that yet. Try another combination.',
    'curriculum.notYet': 'Curriculum not listed? Tell us and we will add it.',
    'country.eg': 'Egypt',
    'country.sa': 'Saudi Arabia',
    'track.national': 'National',
    'track.american': 'American diploma',
    'track.british': 'British / IGCSE',
    'track.ib': 'International Baccalaureate',
    'track.azhar': 'Al-Azhar',
    'subject.math': 'Mathematics',
    'subject.science': 'Science',
    'subject.arabic': 'Arabic',
    'subject.english': 'English',
    'subject.social_studies': 'Social studies',
    'curriculum.needStudent': 'Add a student first, so a curriculum has someone to belong to.',
    'curriculum.stage.queued': 'Queued',
    'curriculum.stage.extracting': 'Reading the book',
    'curriculum.stage.mapping': 'Mapping units and lessons',
    'curriculum.stage.ready_for_review': 'Ready for your review',
    'curriculum.stage.approved': 'Approved',
    'curriculum.stage.failed': 'Processing failed',
    'curriculum.review': 'Review the units and lessons',
    'curriculum.reviewIntro': 'This is how we read the book. Check the page numbers before approving.',
    'curriculum.approve': 'Approve this structure',
    'curriculum.approving': 'Approving…',
    'curriculum.approved': 'Approved. Lessons will follow this.',
    'curriculum.pages': 'pages {from}-{to}',
    'curriculum.checkThese': 'Worth a look:',
    'curriculum.editRange': 'Edit pages',
    'curriculum.save': 'Save',
    'curriculum.cancel': 'Cancel',
    'curriculum.alreadyAnalysed': 'We had already analysed this book, so your review is ready now.',
    'curriculum.failedBody': 'We could not analyse this file. Check that it is a PDF with real text rather than scanned images.',
    'curriculum.failed.too_large_to_extract': 'This file is larger than we can analyse right now (40 MB limit). Try uploading the book in parts, or a single term rather than the whole book.',
    'curriculum.failed.no_text': 'This file looks like scanned images with no text. We cannot read it yet — we need a PDF that contains real text.',
    'curriculum.failed.structure_invalid': 'We read the file but could not identify its units and lessons confidently. We will review it by hand.',
    'curriculum.failed.job_failed': 'Processing stalled. We will retry, and review it by hand if it keeps failing.',
    'curriculum.add': 'Add a curriculum',
    'curriculum.title': 'Curriculum name',
    'curriculum.titleHint': 'For example: Grade 5 maths, first term.',
    'curriculum.student': 'Student',
    'curriculum.create': 'Save curriculum',
    'curriculum.created': 'Curriculum created. Upload the file now.',
    'curriculum.file': 'Curriculum file',
    'curriculum.fileHint': 'One PDF, up to {max} MB. Your file stays private.',
    'curriculum.uploadFile': 'Upload file',
    'curriculum.uploading': 'Uploading…',
    'curriculum.uploaded': 'File uploaded, processing has started.',
    'curriculum.for': 'for {name}',
    'curriculum.noneYet': 'No curricula yet.',
    'curriculum.unavailable': 'Uploads are unavailable right now. Please try again later.',
    'curriculum.pickFile': 'Choose a file first.',
    'status.upload_pending': 'Waiting for a file',
    'status.processing': 'Processing',
    'status.needs_review': 'Waiting for your review',
    'status.approved': 'Approved',
    'status.failed': 'Processing failed',
    'valid.content_type_invalid': 'Only PDF files are accepted.',
    'valid.file_too_large': 'That file is {size} MB. The limit is {max} MB.',
    'valid.email_not_verified': 'Confirm your email address first.',
    'curriculum.blocked': 'Confirm your email first to upload a curriculum.',

    'students.title': 'Students',
    'students.add': 'Add a student',
    'students.empty':
      'No students yet. Add your child so you can upload their curriculum and follow their progress. A nickname is fine.',
    'students.edit': 'Edit',
    'students.remove': 'Remove',
    'students.grade': 'Grade {grade}',
    'students.added': 'Student added.',
    'students.saved': 'Changes saved.',
    'students.removed': 'Student removed.',

    'studentForm.addTitle': 'Add a student',
    'studentForm.editTitle': 'Edit {name}',
    'studentForm.name': "Student's name",
    'studentForm.nameHint': 'A nickname is fine.',
    'studentForm.username': 'Username',
    'studentForm.usernameHint': 'What your child types to sign in. For example: layan.m',
    'studentForm.usernameLocked':
      'This cannot be changed: your child has learned it, and changing it would lock them out.',
    'studentForm.gradeLabel': 'Grade',
    'studentForm.language': "Student's interface language",
    'studentForm.languageHint': "The language your child sees. It does not change yours.",
    'studentForm.password': "Student's password",
    'studentForm.newPassword': 'New password',
    'studentForm.passwordHintCreate':
      'At least {min} characters. You choose it, and a student has no email to recover it with.',
    'studentForm.passwordHintEdit':
      'Leave blank to keep the current one. Changing it ends your student’s active sessions.',
    'studentForm.save': 'Save student',
    'studentForm.saveEdits': 'Save changes',

    'delete.title': 'Remove {name}',
    'delete.body':
      '{name} will lose access immediately. Their learning history is kept, and you can request permanent deletion from privacy settings.',
    'delete.confirmLabel': 'Type "{name}" to confirm',
    'delete.submit': 'Remove student',
    'delete.pending': 'Removing…',

    'student.welcome': 'Hello',
    'student.noLesson': 'No lesson is ready yet. Ask your parent to finish setting up the curriculum.',

    'valid.required': 'This field is required.',
    'valid.invalid_email': 'Enter a valid email address.',
    'valid.password_too_short': 'Password must be at least {min} characters.',
    'valid.password_invalid': 'That password is not acceptable.',
    'valid.username_format': 'Lowercase letters and digits only, 3 to 32 characters.',
    'valid.username_taken': 'That username is taken, please choose another.',
    'valid.grade_invalid': 'Choose a valid grade.',
    'valid.language_invalid': 'Unsupported language.',
    'valid.no_changes': 'Nothing changed.',
  },
};
